
import React, { useState } from 'react';

export const ProductPreview: React.FC = () => {
  const [showMore, setShowMore] = useState(false);

  const libraryItems = [
    { title: 'Filosofia', icon: '📜', bg: 'bg-purple-500/10' },
    { title: 'Matemática', icon: '📐', bg: 'bg-blue-500/10' },
    { title: 'Português', icon: '✍️', bg: 'bg-emerald-500/10' },
    { title: 'IA & Tech', icon: '🤖', bg: 'bg-indigo-500/10' },
    // Expanded items
    { title: 'História', icon: '🏺', bg: 'bg-amber-500/10' },
    { title: 'Geografia', icon: '🌍', bg: 'bg-cyan-500/10' },
    { title: 'Artes', icon: '🎨', bg: 'bg-rose-500/10' },
    { title: 'Ciências', icon: '🧬', bg: 'bg-lime-500/10' },
    { title: 'Economia', icon: '📈', bg: 'bg-yellow-500/10' },
    { title: 'Psicologia', icon: '🧠', bg: 'bg-fuchsia-500/10' },
    { title: 'Sociologia', icon: '👥', bg: 'bg-orange-500/10' },
    { title: 'Idiomas', icon: '🗣️', bg: 'bg-teal-500/10' },
  ];

  const visibleItems = showMore ? libraryItems : libraryItems.slice(0, 4);

  return (
    <section className="pt-12 pb-24 px-4 bg-slate-950 relative overflow-hidden">
      {/* Background glow for the showcase */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-indigo-600/10 blur-[120px] rounded-full -z-10"></div>
      
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-indigo-400 font-semibold tracking-wider uppercase text-sm">Interface do Usuário</span>
          <h2 className="text-3xl md:text-5xl font-bold mt-4 mb-6">Explore o ecossistema Qualle</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Uma plataforma intuitiva desenhada para mapear seu potencial e conectar você ao conhecimento que realmente importa.
          </p>
        </div>

        {/* Mockup Container */}
        <div className="glass rounded-[2.5rem] p-4 md:p-8 shadow-2xl border-white/5 bg-slate-900/40 backdrop-blur-2xl">
          <div className="flex flex-col lg:flex-row gap-8 min-h-[600px]">
            
            {/* Sidebar - Skills & User Profile */}
            <div className="w-full lg:w-1/3 flex flex-col gap-6">
              <div className="bg-white/5 rounded-3xl p-6 border border-white/5">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center font-bold text-lg">R</div>
                  <div>
                    <h4 className="font-bold">Rees Silva</h4>
                    <p className="text-xs text-slate-500">Nível 12 • Explorador</p>
                  </div>
                </div>
              </div>

              {/* Requirements Area */}
              <div className="bg-white/5 rounded-3xl p-6 border border-white/5 flex-grow">
                <h5 className="text-sm font-semibold mb-4 text-slate-300">Competências da jornada</h5>
                <ul className="space-y-4">
                  {[
                    { name: 'HTML & CSS Avançado', progress: 100 },
                    { name: 'Lógica de Programação', progress: 100 },
                    { name: 'JavaScript Moderno (ES6+)', progress: 75 },
                    { name: 'React & Hooks', progress: 40 },
                    { name: 'Consumo de APIs REST', progress: 15 },
                    { name: 'Git & GitHub', progress: 90 }
                  ].map((req, i) => (
                    <li key={i} className="flex flex-col gap-2">
                      <div className="flex items-center gap-3 text-sm text-slate-400">
                        <div className={`w-1.5 h-1.5 rounded-full ${req.progress === 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`}></div>
                        <span className="flex-grow">{req.name}</span>
                        <span className="text-[10px] font-bold text-slate-500">{req.progress}%</span>
                      </div>
                      <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-1000 ${req.progress === 100 ? 'bg-emerald-500' : 'bg-indigo-500/50'}`} 
                          style={{ width: `${req.progress}%` }}
                        ></div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Main Content Area - Materials & Library */}
            <div className="flex-grow flex flex-col gap-6">
              <div className="bg-white/5 rounded-3xl p-6 border border-white/5">
                <div className="flex justify-between items-center mb-6">
                  <h5 className="text-sm font-semibold text-slate-300">Biblioteca de competências</h5>
                  <button 
                    onClick={() => setShowMore(!showMore)}
                    className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors focus:outline-none flex items-center gap-1"
                  >
                    {showMore ? 'ver menos' : 'ver mais'}
                    <svg className={`w-3 h-3 transition-transform ${showMore ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                <div className={`grid grid-cols-2 sm:grid-cols-4 gap-4 transition-all duration-500 ease-in-out`}>
                  {visibleItems.map((item, i) => (
                    <div key={i} className={`${item.bg} p-4 rounded-2xl border border-white/5 text-center group cursor-pointer hover:border-white/20 transition-all animate-in fade-in zoom-in duration-300`}>
                      <span className="text-2xl mb-2 block">{item.icon}</span>
                      <span className="text-xs font-medium text-slate-300">{item.title}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trails Visualization Area */}
              <div className="bg-white/5 rounded-3xl p-6 border border-white/5 flex-grow relative overflow-hidden">
                <h5 className="text-sm font-semibold mb-6 text-slate-300">Sua jornada formativa: <span className="text-indigo-400">Desenvolvimento Web</span></h5>
                
                <div className="relative h-48 flex items-center justify-between px-4">
                  {/* Connecting Line */}
                  <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500 -translate-y-1/2 opacity-20"></div>
                  
                  {/* Trail Nodes */}
                  {[
                    { label: 'Fundamentos', icon: '1', active: true },
                    { label: 'Arquitetura', icon: '2', active: true },
                    { label: 'Frameworks', icon: '3', active: false },
                    { label: 'Projeto Final', icon: '🏆', active: false },
                  ].map((node, i) => (
                    <div key={i} className="relative z-10 flex flex-col items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg border-2 transition-all shadow-xl ${node.active ? 'bg-indigo-600 border-indigo-400 text-white scale-110 shadow-indigo-500/30' : 'bg-slate-800 border-slate-700 text-slate-500'}`}>
                        {node.icon}
                      </div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${node.active ? 'text-white' : 'text-slate-600'}`}>
                        {node.label}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-6 bg-white/5 rounded-2xl border border-white/5">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Progresso da Jornada</span>
                    <span className="text-xs font-bold text-indigo-400">50%</span>
                  </div>
                  <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};
